import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatRupiah } from "../lib/api";
import { ArrowRight, Sparkles, Heart, Brain, BookOpen, Calendar, Star, Quote, Lock, ListChecks } from "lucide-react";
import HeroCarousel from "../components/HeroCarousel";

export default function Home() {
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [events, setEvents] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [tests, setTests] = useState([]);
  const [settings, setSettings] = useState({});

  useEffect(() => {
    api.get("/products?featured=true").then(r => Array.isArray(r.data) && setProducts(r.data.slice(0, 3))).catch(() => {});
    api.get("/services").then(r => Array.isArray(r.data) && setServices(r.data.slice(0, 4))).catch(() => {});
    api.get("/events").then(r => Array.isArray(r.data) && setEvents(r.data.slice(0, 3))).catch(() => {});
    api.get("/testimonials").then(r => Array.isArray(r.data) && setTestimonials(r.data.filter(t => t.is_featured).slice(0, 3))).catch(() => {});
    api.get("/tests").then(r => Array.isArray(r.data) && setTests(r.data.slice(0, 4))).catch(() => {});
    api.get("/settings").then(r => { if(r.data && typeof r.data === 'object') setSettings(r.data) }).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden lumina-bg-soft pt-16 md:pt-24 pb-24 md:pb-32">
        <div className="lumina-blob w-[500px] h-[500px] bg-[#9D84D2] -top-32 -left-32 animate-float"></div>
        <div className="lumina-blob w-[400px] h-[400px] bg-[#E8F0FA] top-40 -right-20 animate-float" style={{ animationDelay: "2s" }}></div>

        <div className="lumina-container relative z-10 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div className="animate-fade-in-up">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/60 backdrop-blur border border-white text-xs tracking-[0.2em] uppercase text-[#6B4BA3] font-semibold mb-6">
              <Sparkles className="w-3.5 h-3.5" /> Welcome to HearInsight
            </div>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[1.05] text-[#1A1728] mb-6">
              {settings.hero_title || <>Cahaya untuk <em className="text-[#6B4BA3] not-italic font-normal italic" style={{fontStyle:'italic'}}>pikiran</em> yang tenang.</>}
            </h1>
            <p className="text-lg text-[#5C5870] leading-relaxed mb-10 max-w-xl">
              {settings.hero_subtitle || settings.about || "Ruang aman untuk perjalanan kesehatan mentalmu — bersama psikolog berlisensi, produk digital yang dirancang dengan hati, dan komunitas yang hangat."}
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/services" className="lumina-btn-primary" data-testid="hero-cta-konsultasi">
                Konsultasi Sekarang <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/products" className="lumina-btn-outline" data-testid="hero-cta-produk">
                Lihat Produk
              </Link>
            </div>
            <div className="flex gap-8 mt-12 pt-8 border-t border-[#E2DFED]">
              <div><div className="font-serif text-3xl text-[#6B4BA3]">10K+</div><div className="text-xs text-[#5C5870] uppercase tracking-wider mt-1">Klien Terbantu</div></div>
              <div><div className="font-serif text-3xl text-[#6B4BA3]">50+</div><div className="text-xs text-[#5C5870] uppercase tracking-wider mt-1">Mitra Perusahaan</div></div>
              <div><div className="font-serif text-3xl text-[#6B4BA3]">15+</div><div className="text-xs text-[#5C5870] uppercase tracking-wider mt-1">Psikolog</div></div>
            </div>
          </div>

          <div className="relative animate-fade-in-up animate-delay-2">
            <div className="relative">
              <img 
                src={settings.hero_image?.startsWith("/") ? (process.env.REACT_APP_BACKEND_URL || "") + settings.hero_image : (settings.hero_image || "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?q=80&w=900&auto=format&fit=crop")} 
                alt="Konsultasi" 
                className="rounded-[2.5rem] shadow-2xl shadow-[#6B4BA3]/15 w-full object-cover h-[560px]" 
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-3xl p-5 shadow-xl shadow-[#6B4BA3]/10 max-w-[240px] animate-float">
                <div className="flex items-center gap-2 mb-2">
                  <div className="flex">{[...Array(5)].map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-[#FFB74D] text-[#FFB74D]" />)}</div>
                </div>
                <p className="text-sm text-[#1A1728] font-medium leading-snug">"Membantu saya menemukan kembali ketenangan."</p>
                <p className="text-xs text-[#5C5870] mt-2">— Sarah, klien</p>
              </div>
              <div className="absolute -top-6 -right-6 bg-[#E8F0FA] rounded-3xl p-5 max-w-[200px] animate-float" style={{ animationDelay: "1s" }}>
                <Heart className="w-6 h-6 text-[#6B4BA3] mb-2" />
                <p className="text-sm text-[#1A1728] font-medium leading-snug">Pendampingan dengan empati & profesionalitas.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROMO CAROUSEL */}
      <HeroCarousel />

      {/* SERVICES */}
      <section className="lumina-container py-24" data-testid="home-services">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
          <div>
            <div className="lumina-label mb-3">Layanan Kami</div>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-[#1A1728]">Konseling, workshop & lebih.</h2>
          </div>
          <Link to="/services" className="text-[#6B4BA3] font-medium flex items-center gap-1 hover:gap-2 transition-all">Lihat semua <ArrowRight className="w-4 h-4" /></Link>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((s, idx) => {
            const Icon = { counseling: Heart, workshop: BookOpen, training: Brain, seminar: Calendar }[s.type] || Sparkles;
            return (
              <Link to={`/services/${s.slug}`} key={s.id} className="lumina-card p-7 group" data-testid={`home-service-${s.slug}`}>
                <div className="w-12 h-12 rounded-2xl bg-[#E8F0FA] flex items-center justify-center mb-5 group-hover:bg-[#6B4BA3] group-hover:text-white transition-colors text-[#6B4BA3]">
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="font-serif text-2xl text-[#1A1728] mb-2 leading-tight">{s.title}</h3>
                <p className="text-sm text-[#5C5870] leading-relaxed mb-4 line-clamp-2">{s.description}</p>
                <div className="text-[#6B4BA3] font-semibold">{formatRupiah(s.price)}</div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* DIGITAL PRODUCTS */}
      <section className="bg-[#E8F0FA]/40 py-24" data-testid="home-products">
        <div className="lumina-container">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
            <div>
              <div className="lumina-label mb-3">Produk Digital Pilihan</div>
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-[#1A1728]">Tools cantik untuk hari-hari penuh makna.</h2>
            </div>
            <Link to="/products" className="text-[#6B4BA3] font-medium flex items-center gap-1 hover:gap-2 transition-all">Lihat semua <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {products.map(p => (
              <Link to={`/products/${p.slug}`} key={p.id} className="lumina-card overflow-hidden group" data-testid={`home-product-${p.slug}`}>
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-6">
                  <span className="lumina-label">{p.category.replace("_", " ")}</span>
                  <h3 className="font-serif text-2xl text-[#1A1728] mt-2 mb-3 leading-tight">{p.title}</h3>
                  <p className="text-sm text-[#5C5870] leading-relaxed mb-4 line-clamp-2">{p.description}</p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-[#6B4BA3] font-semibold text-lg">{formatRupiah(p.price)}</span>
                    {p.original_price && p.original_price > p.price && (
                      <span className="text-[#5C5870] line-through text-sm">{formatRupiah(p.original_price)}</span>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* EVENTS */}
      <section className="lumina-container py-24" data-testid="home-events">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
          <div>
            <div className="lumina-label mb-3">Event Mendatang</div>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-[#1A1728]">Tumbuh bersama komunitas.</h2>
          </div>
          <Link to="/events" className="text-[#6B4BA3] font-medium flex items-center gap-1 hover:gap-2 transition-all">Semua event <ArrowRight className="w-4 h-4" /></Link>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {events.map(e => (
            <Link to={`/events/${e.slug}`} key={e.id} className="lumina-card overflow-hidden group" data-testid={`home-event-${e.slug}`}>
              <div className="aspect-[16/10] overflow-hidden relative">
                <img src={e.image} alt={e.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute top-4 left-4 bg-white/90 backdrop-blur rounded-full px-3 py-1 text-xs font-medium text-[#6B4BA3]">
                  {new Date(e.date).toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "numeric" })}
                </div>
              </div>
              <div className="p-6">
                <h3 className="font-serif text-xl text-[#1A1728] mb-2 leading-tight">{e.title}</h3>
                <p className="text-xs text-[#5C5870] mb-3">📍 {e.location}</p>
                <div className="text-[#6B4BA3] font-semibold">{e.price === 0 ? "GRATIS" : formatRupiah(e.price)}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* TESTS SECTION */}
      <section className="lumina-container py-24" data-testid="home-tests">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
          <div>
            <div className="lumina-label mb-3">Self Test</div>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-[#1A1728]">Kenali dirimu dalam 20 menit.</h2>
          </div>
          <Link to="/tests" className="text-[#6B4BA3] font-medium flex items-center gap-1 hover:gap-2 transition-all">Semua tes <ArrowRight className="w-4 h-4" /></Link>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {tests.map(t => (
            <Link to={`/tests/${t.slug}`} key={t.id} className="lumina-card overflow-hidden group" data-testid={`home-test-${t.slug}`}>
              <div className="aspect-[4/3] overflow-hidden relative">
                <img src={t.image} alt={t.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute top-3 left-3 bg-white/90 backdrop-blur rounded-full px-2.5 py-1 text-[10px] font-medium text-[#6B4BA3] uppercase tracking-wider flex items-center gap-1">
                  {t.price === 0 ? <Sparkles className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
                  {t.price === 0 ? "Gratis" : "Premium"}
                </div>
              </div>
              <div className="p-5">
                <h3 className="font-serif text-lg text-[#1A1728] mb-2 leading-tight">{t.title}</h3>
                <div className="text-xs text-[#5C5870] mb-3 flex items-center gap-1"><ListChecks className="w-3 h-3" /> {t.duration_minutes} mnt</div>
                <div className="text-sm text-[#6B4BA3] font-semibold">{t.price === 0 ? "Mulai gratis" : formatRupiah(t.price)}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-[#1A1728] text-white py-24" data-testid="home-testimonials">
        <div className="lumina-container">
          <div className="lumina-label !text-[#9D84D2] mb-3">Cerita Mereka</div>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tight mb-16 max-w-3xl">Perubahan kecil setiap hari, dampaknya seumur hidup.</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map(t => (
              <div key={t.id} className="bg-white/5 backdrop-blur rounded-3xl p-8 border border-white/10">
                <Quote className="w-8 h-8 text-[#9D84D2] mb-4" />
                <p className="text-white/90 leading-relaxed mb-6">"{t.content}"</p>
                <div className="flex items-center gap-3 pt-4 border-t border-white/10">
                  {t.image && <img src={t.image} alt={t.name} className="w-10 h-10 rounded-full object-cover" />}
                  <div>
                    <div className="font-medium">{t.name}</div>
                    <div className="text-xs text-white/60">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="lumina-container py-24" data-testid="home-cta">
        <div className="bg-gradient-to-br from-[#E8F0FA] via-white to-[#F1EBFA] rounded-[2.5rem] p-12 md:p-20 text-center border border-[#E2DFED]">
          <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-[#1A1728] mb-5 max-w-2xl mx-auto">Mulai perjalanan tenang-mu hari ini.</h2>
          <p className="text-[#5C5870] mb-8 max-w-xl mx-auto">Booking sesi pertamamu dalam 2 menit. Konsultasi 1-on-1 dengan psikolog kami.</p>
          <Link to="/services" className="lumina-btn-primary inline-flex" data-testid="cta-bottom">Booking Sekarang <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </section>
    </div>
  );
}
